/**
 * How much room is left in this period, before a payment has to move.
 *
 * One measure, one bar, one scale — the cap is the full width and the bar is
 * what is already counted against it. No second axis and no second series: the
 * only question is "will the next payment fit", and anything else on this chart
 * makes that harder to read, not easier.
 *
 * The period dates are printed in full because the federal fiscal quarter
 * starts on 1 October, and a reader who assumes a calendar quarter is a month
 * out. Date-only values are formatted without timezone conversion for the same
 * reason — a one-day shift names the wrong quarter.
 */

import React from 'react';
import { CalendarClock, AlertTriangle } from 'lucide-react';
import { formatDateOnly } from '../../utils/dateTime';
import { PERIOD_LABELS, type PeriodEnvelopeState, type PaymentEnvelope } from './types';

const money = (v: string | number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(v));

interface Props {
  envelope: PeriodEnvelopeState;
  /** A payment being considered, drawn as the slice that would be added. */
  proposedAmount?: number | null;
  className?: string;
}

export const PeriodHeadroomMeter: React.FC<Props> = ({ envelope, proposedAmount, className = '' }) => {
  const cap = Number(envelope.period_cap);
  const used = Number(envelope.period_total);
  const headroom = Number(envelope.headroom);
  const proposed = Math.max(0, proposedAmount ?? 0);

  const usedPct = cap > 0 ? Math.min(100, (used / cap) * 100) : 0;
  const proposedPct = cap > 0 ? Math.min(100 - usedPct, (proposed / cap) * 100) : 0;
  const breaches = used + proposed > cap;
  const overshoot = breaches ? used + proposed - cap : 0;

  const periodLabel =
    PERIOD_LABELS[envelope.period_type as PaymentEnvelope['period_type']] ?? envelope.period_type;

  return (
    <div className={`rounded-lg border border-border bg-card p-4 ${className}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-medium text-foreground">This period</p>
        <p className="text-xs text-muted-foreground">
          {formatDateOnly(envelope.period_start)} – {formatDateOnly(envelope.period_end)}
        </p>
      </div>

      <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
        {money(headroom)}{' '}
        <span className="text-sm font-normal text-muted-foreground">
          left of {money(cap)}
        </span>
      </p>

      {/* One scale: the track is the cap. */}
      <div
        className="mt-3 flex h-2.5 w-full overflow-hidden rounded-full bg-muted"
        role="img"
        aria-label={`${money(used)} of ${money(cap)} used this period, ${money(headroom)} remaining`}
      >
        <div
          className="h-full rounded-l-full bg-sky-600 dark:bg-sky-500"
          style={{ width: `${usedPct}%` }}
        />
        {proposedPct > 0 && (
          // A 2px surface gap so the proposed slice reads as a separate mark
          // rather than more of the same bar.
          <div
            className={`h-full border-l-2 border-card ${
              breaches ? 'bg-orange-500' : 'bg-sky-400 dark:bg-sky-300'
            }`}
            style={{ width: `${proposedPct}%` }}
          />
        )}
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs tabular-nums sm:grid-cols-3">
        <div>
          <dt className="text-muted-foreground">Already counted</dt>
          <dd className="font-medium text-foreground">{money(used)}</dd>
        </div>
        {proposed > 0 && (
          <div>
            <dt className="text-muted-foreground">This payment</dt>
            <dd className="font-medium text-foreground">{money(proposed)}</dd>
          </div>
        )}
        <div>
          <dt className="text-muted-foreground">Ceiling</dt>
          <dd className="font-medium text-foreground">{money(cap)}</dd>
        </div>
      </dl>

      {breaches && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-orange-300 bg-orange-50 p-2 dark:border-orange-800 dark:bg-orange-950">
          {envelope.enforcement === 'block' ? (
            <CalendarClock
              className="mt-0.5 h-4 w-4 flex-shrink-0 text-orange-700 dark:text-orange-300"
              aria-hidden="true"
            />
          ) : (
            <AlertTriangle
              className="mt-0.5 h-4 w-4 flex-shrink-0 text-orange-700 dark:text-orange-300"
              aria-hidden="true"
            />
          )}
          <p className="text-xs text-orange-900 dark:text-orange-100">
            {envelope.enforcement === 'block' ? (
              <>
                <strong>{money(overshoot)} over.</strong> This payment will be scheduled into the
                next period rather than refused — she does not lose it.
              </>
            ) : (
              <>
                <strong>{money(overshoot)} over.</strong> Enforcement is set to <em>warn</em>, so
                this will be paid anyway and recorded for a counsellor.
              </>
            )}
          </p>
        </div>
      )}

      <p className="mt-3 border-t border-border pt-2 text-xs text-muted-foreground">
        {periodLabel}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">{envelope.cap_basis}</p>
    </div>
  );
};

export default PeriodHeadroomMeter;
